import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, AlertTriangle, ShieldOff } from 'lucide-react';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  border: '1px solid var(--border-subtle, #E2E8F0)',
  radius: { sm: '8px', md: '10px', lg: '14px', xl: '18px', full: '999px' },
  shadow: { xs: '0 1px 3px rgba(0,0,0,0.06)', sm: '0 2px 8px rgba(0,0,0,0.08)', lg: '0 8px 32px rgba(0,0,0,0.10)' },
};

// ─── Device icon row ──────────────────────────────────────────────────────────
const DeviceTag = ({ label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', borderRadius: T.radius.full,
    background: '#FFF1F2', border: '1px solid #FECDD3',
    fontSize: '0.75rem', fontWeight: 600, color: '#BE123C',
  }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#FB7185', display: 'inline-block' }} />
    {label}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const LogoutAllDevices = ({ onCancel, onConfirm }) => {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const handleCancel = () => {
    if (onCancel) { onCancel(); return; }
    navigate(-1);
  };

  const handleLogout = async () => {
    if (onConfirm) { onConfirm(); return; }
    setConfirming(true);
    await new Promise(r => setTimeout(r, 700));
    localStorage.clear();
    navigate('/CollegeLogin', { replace: true });
  };

  return (
    <div className="college-main-layout" style={{ background: '#F8FAFC' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content" style={{ padding: '32px 28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 500 }}>

            {/* Confirmation card */}
            <div style={{
              background: '#FFFFFF', borderRadius: T.radius.xl,
              border: '1px solid #FECACA', boxShadow: '0 8px 40px rgba(190,18,60,0.12)',
              overflow: 'hidden', position: 'relative',
            }}>
              {/* 4px accent strip */}
              <div style={{ height: 4, background: 'linear-gradient(90deg, #BE123C, #DC2626, #EA580C)', flexShrink: 0 }} />

              <div style={{ padding: '36px 36px 32px' }}>

                {/* Icon circle */}
                <div style={{
                  width: 68, height: 68, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FFE4E6, #FECDD3)',
                  border: '2px solid #FDA4AF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                }}>
                  <ShieldOff size={32} style={{ color: '#BE123C' }} />
                </div>

                {/* Title */}
                <h2 style={{
                  fontSize: '1.375rem', fontWeight: 800, color: '#0F172A',
                  textAlign: 'center', margin: '0 0 10px', letterSpacing: '-0.02em',
                }}>
                  Logout from all devices?
                </h2>

                {/* Subtitle */}
                <p style={{
                  fontSize: '0.875rem', color: '#64748B', textAlign: 'center',
                  lineHeight: 1.7, margin: '0 0 20px',
                }}>
                  This will end <strong style={{ color: '#374151' }}>all active sessions</strong> across every device
                  where you are currently logged in. This action cannot be undone.
                </p>

                {/* Device tags */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
                  {['Desktop', 'Mobile', 'Tablet', 'Other sessions'].map(d => (
                    <DeviceTag key={d} label={d} />
                  ))}
                </div>

                {/* Warning banner */}
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '12px 14px', borderRadius: T.radius.md,
                  background: '#FFF1F2', border: '1px solid #FECDD3',
                  marginBottom: 28,
                }}>
                  <AlertTriangle size={16} style={{ color: '#BE123C', flexShrink: 0, marginTop: 1 }} />
                  <p style={{ fontSize: '0.8125rem', color: '#9F1239', margin: 0, lineHeight: 1.6 }}>
                    All devices will be signed out immediately. You will need to log in again on each device to regain access.
                  </p>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={handleCancel}
                    disabled={confirming}
                    style={{
                      flex: 1, padding: '12px 0',
                      background: '#F8FAFC', border: T.border,
                      borderRadius: T.radius.md, cursor: confirming ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem', fontWeight: 700, color: '#374151',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!confirming) { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.borderColor = '#CBD5E1'; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogout}
                    disabled={confirming}
                    style={{
                      flex: 1, padding: '12px 0',
                      background: confirming ? '#FDA4AF' : 'linear-gradient(135deg, #BE123C, #9F1239)',
                      border: 'none', borderRadius: T.radius.md,
                      cursor: confirming ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem', fontWeight: 700, color: '#FFFFFF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      boxShadow: confirming ? 'none' : '0 4px 14px rgba(190,18,60,0.40)',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!confirming) e.currentTarget.style.boxShadow = '0 6px 20px rgba(190,18,60,0.50)'; }}
                    onMouseLeave={e => { if (!confirming) e.currentTarget.style.boxShadow = '0 4px 14px rgba(190,18,60,0.40)'; }}
                  >
                    {confirming
                      ? <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Logging out…</>
                      : <><LogOut size={15} /> Logout All</>
                    }
                  </button>
                </div>

              </div>
            </div>

            {/* Helper note */}
            <p style={{ textAlign: 'center', fontSize: '0.78rem', color: '#94A3B8', marginTop: 16 }}>
              All sessions on all devices will be terminated simultaneously.
            </p>

          </div>
        </main>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default LogoutAllDevices;
